'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Zap, Globe, Shield, CheckCircle, Code, Link2, Database, Cloud, Smartphone, Settings, Lock } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const apisIntegrationData = {
  title: "APIs & Integration",
  subtitle: "Connect, integrate, and orchestrate systems seamlessly with robust API development, third-party integrations, and microservices architecture solutions",
  headerGradient: "bg-gradient-to-r from-purple-600 via-indigo-500 to-blue-500",
  accentColor: "text-purple-600",
  buttonColor: "bg-purple-600 hover:bg-purple-700",
  
  overview: "APIs & Integration services enable seamless connectivity between different systems, applications, and services. Our comprehensive integration solutions include RESTful API development, GraphQL implementations, microservices architecture, third-party service integrations, and enterprise system orchestration. We help businesses create scalable, secure, and efficient integration ecosystems that enhance operational efficiency and enable digital transformation.",
  
  capabilities: [
    {
      name: "API Development",
      description: "Design and build robust, scalable APIs that enable seamless data exchange and system communication",
      icon: Code,
      applications: ["RESTful APIs", "GraphQL APIs", "WebSocket APIs", "Webhook Systems"],
      useCases: ["Data exchange", "Service communication", "Real-time updates", "Event-driven architecture"]
    },
    {
      name: "System Integration",
      description: "Connect disparate systems and applications to create unified, efficient business processes",
      icon: Link2,
      applications: ["Enterprise Integration", "Cloud Integration", "Database Integration", "Legacy System Integration"],
      useCases: ["Data synchronization", "Process automation", "System modernization", "Workflow optimization"]
    },
    {
      name: "Microservices Architecture",
      description: "Build scalable, maintainable applications using microservices patterns and containerization",
      icon: Settings,
      applications: ["Service Mesh", "Container Orchestration", "API Gateway", "Service Discovery"],
      useCases: ["Scalable architecture", "Independent deployment", "Technology diversity", "Fault isolation"]
    }
  ],
  
  technologies: [
    {
      category: "API Frameworks",
      tools: ["Express.js", "FastAPI", "Spring Boot", "ASP.NET Core", "Django REST", "Flask"],
      description: "Modern frameworks for building robust and scalable APIs"
    },
    {
      category: "Integration Platforms",
      tools: ["MuleSoft", "Apache Camel", "Zapier", "Microsoft Logic Apps", "AWS Step Functions", "Google Cloud Workflows"],
      description: "Enterprise-grade integration platforms and workflow engines"
    },
    {
      category: "API Management",
      tools: ["Kong", "AWS API Gateway", "Azure API Management", "Google Cloud Endpoints", "Postman", "Swagger/OpenAPI"],
      description: "Tools for API lifecycle management, documentation, and monitoring"
    },
    {
      category: "Message Brokers",
      tools: ["Apache Kafka", "RabbitMQ", "Redis", "Amazon SQS", "Azure Service Bus", "Google Pub/Sub"],
      description: "Reliable message queuing and event streaming platforms"
    },
    {
      category: "Containerization",
      tools: ["Docker", "Kubernetes", "OpenShift", "Docker Swarm", "Helm", "Istio"],
      description: "Container orchestration and service mesh technologies"
    },
    {
      category: "Monitoring & Testing",
      tools: ["Postman", "Insomnia", "Newman", "JMeter", "Prometheus", "Grafana"],
      description: "API testing, monitoring, and performance analysis tools"
    }
  ],
  
  integrationTypes: [
    {
      type: "Enterprise Application Integration (EAI)",
      description: "Connect internal enterprise systems and applications",
      examples: ["ERP Integration", "CRM Integration", "HR Systems", "Financial Systems", "Supply Chain", "Inventory Management"],
      useCases: ["Data consistency", "Process automation", "Real-time reporting", "Workflow optimization"]
    },
    {
      type: "Cloud Integration",
      description: "Connect cloud services and hybrid environments",
      examples: ["Multi-cloud Integration", "SaaS Integration", "Hybrid Cloud", "Cloud Migration", "Serverless Integration", "API Gateway"],
      useCases: ["Cloud adoption", "Service orchestration", "Data migration", "Scalability"]
    },
    {
      type: "Third-party Integrations",
      description: "Connect with external services and partner systems",
      examples: ["Payment Gateways", "Social Media APIs", "Shipping Services", "Marketing Tools", "Analytics Platforms", "Communication APIs"],
      useCases: ["Feature enhancement", "Service expansion", "Partner connectivity", "Customer experience"]
    },
    {
      type: "Data Integration",
      description: "Synchronize and transform data across multiple sources",
      examples: ["ETL Pipelines", "Data Warehousing", "Real-time Sync", "Data Lakes", "Stream Processing", "Data Transformation"],
      useCases: ["Data analytics", "Business intelligence", "Reporting", "Data governance"]
    }
  ],
  
  industries: [
    {
      name: "Financial Services",
      applications: ["Payment processing", "Banking APIs", "Trading systems", "Compliance reporting"],
      impact: "50% faster transaction processing, 99.9% uptime reliability"
    },
    {
      name: "E-commerce",
      applications: ["Payment gateways", "Inventory systems", "Shipping APIs", "Customer management"],
      impact: "40% reduction in checkout abandonment, seamless user experience"
    },
    {
      name: "Healthcare",
      applications: ["EHR integration", "Medical device APIs", "Insurance systems", "Telemedicine platforms"],
      impact: "Improved patient care, 60% reduction in administrative overhead"
    },
    {
      name: "Manufacturing",
      applications: ["IoT device integration", "Supply chain systems", "Quality control", "Production monitoring"],
      impact: "30% improvement in operational efficiency, real-time visibility"
    },
    {
      name: "Retail",
      applications: ["POS systems", "Inventory management", "Customer loyalty", "Marketing automation"],
      impact: "Enhanced customer experience, 25% increase in operational efficiency"
    },
    {
      name: "Logistics",
      applications: ["Fleet management", "Route optimization", "Tracking systems", "Warehouse management"],
      impact: "35% reduction in delivery times, improved tracking accuracy"
    }
  ],
  
  apiPatterns: [
    {
      pattern: "RESTful APIs",
      description: "Standard HTTP-based APIs following REST architectural principles",
      benefits: ["Simple and intuitive", "Stateless communication", "Cacheable responses", "Uniform interface"],
      useCases: ["Web services", "Mobile backends", "Microservices", "Public APIs"]
    },
    {
      pattern: "GraphQL APIs",
      description: "Query language and runtime for APIs with flexible data fetching",
      benefits: ["Single endpoint", "Flexible queries", "Strong typing", "Real-time subscriptions"],
      useCases: ["Complex data requirements", "Mobile applications", "Real-time features", "Developer experience"]
    },
    {
      pattern: "Event-Driven Architecture",
      description: "Asynchronous communication using events and message brokers",
      benefits: ["Loose coupling", "Scalability", "Resilience", "Real-time processing"],
      useCases: ["Microservices", "Real-time systems", "Event sourcing", "CQRS patterns"]
    },
    {
      pattern: "API Gateway Pattern",
      description: "Centralized entry point for managing API requests and responses",
      benefits: ["Request routing", "Authentication", "Rate limiting", "Monitoring"],
      useCases: ["Microservices", "API management", "Security enforcement", "Traffic control"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Requirements Analysis & Planning",
      description: "Understand integration needs, existing systems, and business objectives",
      details: ["System assessment", "Integration mapping", "Requirements gathering", "Architecture planning"]
    },
    {
      step: "2", 
      title: "API Design & Documentation",
      description: "Design API contracts, data models, and comprehensive documentation",
      details: ["API specification", "Data modeling", "Security design", "Documentation creation"]
    },
    {
      step: "3",
      title: "Development & Implementation",
      description: "Build APIs, integration layers, and connection mechanisms",
      details: ["API development", "Integration coding", "Error handling", "Performance optimization"]
    },
    {
      step: "4",
      title: "Testing & Quality Assurance",
      description: "Comprehensive testing of APIs, integrations, and data flows",
      details: ["Unit testing", "Integration testing", "Performance testing", "Security testing"]
    },
    {
      step: "5",
      title: "Deployment & Monitoring",
      description: "Deploy integrations and establish monitoring and alerting systems",
      details: ["Production deployment", "Monitoring setup", "Alerting configuration", "Performance tracking"]
    },
    {
      step: "6",
      title: "Maintenance & Optimization",
      description: "Ongoing maintenance, updates, and performance optimization",
      details: ["Regular updates", "Performance tuning", "Security patches", "Feature enhancements"]
    }
  ],
  
  benefits: [
    "Seamless system connectivity and data flow",
    "Reduced manual processes and human error",
    "Improved operational efficiency by 40%",
    "Enhanced customer experience and satisfaction", 
    "Real-time data synchronization across systems",
    "Scalable architecture supporting business growth",
    "Faster time-to-market for new features",
    "Better data visibility and business insights"
  ],
  
  challenges: [
    {
      challenge: "Security & Authentication",
      solution: "OAuth 2.0, JWT tokens, API keys, and comprehensive security protocols",
      description: "Ensure secure communication and proper authentication across all integrations"
    },
    {
      challenge: "Performance & Scalability",
      solution: "Caching strategies, load balancing, and horizontal scaling techniques",
      description: "Maintain high performance and handle increasing loads efficiently"
    },
    {
      challenge: "Data Consistency",
      solution: "Transaction management, eventual consistency patterns, and data validation",
      description: "Ensure data integrity across distributed systems and services"
    },
    {
      challenge: "Version Management",
      solution: "API versioning strategies, backward compatibility, and deprecation policies",
      description: "Manage API evolution while maintaining compatibility with existing clients"
    }
  ]
};

export default function APIsIntegrationPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${apisIntegrationData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(apisIntegrationData.title)}
              </h1>
              <p className="text-xl text-purple-100 mb-8 leading-relaxed">
                {localize(apisIntegrationData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["RESTful", "GraphQL", "Microservices", "Integration"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Link2 className="w-12 h-12 mx-auto mb-3 text-purple-200" />
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-purple-100">{localize("API Uptime")}</div>
                  </div>
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-purple-200" />
                    <div className="text-2xl font-bold">50ms</div>
                    <div className="text-sm text-purple-100">{localize("Response Time")}</div>
                  </div>
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-purple-200" />
                    <div className="text-2xl font-bold">Enterprise</div>
                    <div className="text-sm text-purple-100">{localize("Security")}</div>
                  </div>
                  <div className="text-center">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-purple-200" />
                    <div className="text-2xl font-bold">Global</div>
                    <div className="text-sm text-purple-100">{localize("Scale")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${apisIntegrationData.accentColor}`}>
              {localize("What are APIs & Integration?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(apisIntegrationData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core API & Integration Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {apisIntegrationData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-purple-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-purple-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Integration Types Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Types of Integration Solutions")}
          </motion.h2>
          
          <div className="space-y-8">
            {apisIntegrationData.integrationTypes.map((type, index) => (
              <motion.div key={type.type} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(type.type)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(type.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Examples")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {type.examples.map((example) => (
                            <Badge key={example} variant="secondary" className="text-xs">
                              {localize(example)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Common Use Cases")}
                        </h4>
                        <ul className="space-y-2">
                          {type.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* API Patterns Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("API Design Patterns & Architectures")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {apisIntegrationData.apiPatterns.map((pattern, index) => (
              <motion.div key={pattern.pattern} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(pattern.pattern)}</CardTitle>
                    <CardDescription className="text-base">{localize(pattern.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Benefits")}
                        </h4>
                        <ul className="space-y-1">
                          {pattern.benefits.map((benefit) => (
                            <li key={benefit} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(benefit)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {pattern.useCases.map((useCase) => (
                            <Badge key={useCase} variant="outline" className="text-xs">
                              {localize(useCase)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("APIs & Integration Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apisIntegrationData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-purple-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* API Integration Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("API & Integration Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {apisIntegrationData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-purple-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apisIntegrationData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-purple-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our API & Integration Development Process")}
          </h2>
          
          <div className="space-y-8">
            {apisIntegrationData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 md:p-12 border border-purple-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose APIs & Integration?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {apisIntegrationData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Connect Your Systems?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our API and integration experts help you build seamless connections that drive efficiency and enable digital transformation.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${apisIntegrationData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Integration Project")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 